import type { FtueFilter, FtuePlatform, FtueProgression } from '@/types';
import { dayLabel, fmtNum } from '../bq-insights/bqMath';

const DASH = '—';

/** Days of data the progression table waits for before it is called final. */
const WINDOW_DAYS = 7;

/** 'early, 3 of 7 days' while the numbers are not final; null once they are. */
export function earlyLabel(p: FtueProgression): string | null {
  if (p.status !== 'early') return null;
  return p.earlyDays === null
    ? 'early'
    : `early, ${Number(p.earlyDays.toFixed(1))} of ${WINDOW_DAYS} days`;
}

/**
 * Final progression covers only the finishers who have had their 7 days; says so while some are still inside
 * them. Null when the numbers are early (the early label says it) or everyone is counted.
 */
export function partialBaseLabel(p: FtueProgression): string | null {
  if (p.status !== 'final' || p.base >= p.finishers) return null;
  return `7-day figures for ${fmtNum(p.base)} of ${fmtNum(p.finishers)} tutorial finishers; the rest are still inside their 7 days.`;
}

const PLATFORMS: Record<FtuePlatform, string> = {
  all: 'all platforms',
  android: 'Android',
  ios: 'iOS',
};

/** '0.4.4 · all platforms · 17 Oct – 20 Oct'; a one-day range shows the day once. */
export function filterLabel(f: FtueFilter): string {
  const version =
    f.version === 'all' ? 'all versions' : f.version === 'unknown' ? 'unknown version' : f.version;
  const range = f.from === f.to ? dayLabel(f.from) : `${dayLabel(f.from)} – ${dayLabel(f.to)}`;
  return [version, PLATFORMS[f.platform], range].join(' · ');
}

/**
 * 3.6 → '3.6 min', 92 → '92 min', 125 → '2 h 5 min', 120 → '2 h', 0.4 → '< 1 min'; null → '—'.
 * The server's medians are whole-minute buckets, so 0 means under a minute, not no time at all.
 */
export function fmtMinutes(m: number | null): string {
  if (m === null) return DASH;
  if (m < 1) return '< 1 min';
  if (m < 10) return `${Number(m.toFixed(1))} min`;
  const rounded = Math.round(m);
  if (rounded < 120) return `${rounded} min`;
  const h = Math.floor(rounded / 60);
  const min = rounded % 60;
  return min === 0 ? `${h} h` : `${h} h ${min} min`;
}

/**
 * Hours from the end of the tutorial: 20 → '20 h', 36 → '1.5 days', 24 → '1 day', 0 → '< 1 h'; null → '—'.
 * The server's medians are whole-hour buckets, so 0 means under an hour.
 */
export function fmtElapsed(hours: number | null): string {
  if (hours === null) return DASH;
  if (hours < 1) return '< 1 h';
  if (hours < 24) return `${Math.round(hours)} h`;
  const days = Number((hours / 24).toFixed(1));
  return days === 1 ? '1 day' : `${days} days`;
}

/** True when `n` players are too few for the figure to be trusted. */
export function tooFew(n: number, min: number): boolean {
  return n < min;
}
